import { Fragment, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { formatDistanceToNowStrict } from "date-fns";
import {
  CopyField,
  DataTable,
  localTablePage,
  Mono,
  Progress,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  TableIdentity,
  updateTableState,
  type DataTableColumn,
  type DataTableState,
} from "@h2j-data-team/ui";
import type { ReloadAppErrors, ReloadErrorGroup } from "@repo/shared";

import { KpiCell, KpiStrip } from "@/components/app-analysis/kpi";
import { DensityToggle } from "@/components/density-toggle";
import { supportCode } from "@/components/error-screen";
import { reloadErrorsQueryOptions } from "@/features/reloads/api";
import { useFormatDate } from "@/hooks/use-format-date";
import { useLocale } from "@/hooks/use-locale";
import type { ReloadPeriodParams } from "@/lib/api/reloads";
import { listRefreshing, paginationLabels, tableMessages, tableSearch } from "@/lib/ui-labels";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import { useTableDensity } from "@/lib/use-table-density";
import { toast } from "@/components/shared";
import { ViewRunsButton } from "./AppRunsDrawer";
import {
  appLabel,
  failureReason,
  shortFailureReason,
  SITUATION_TONE,
  SITUATIONS,
  situationOf,
  type Situation,
} from "./labels";
import { QlikAppLink } from "./QlikAppLink";

const PAGE_SIZE = 50;
// The Select's "no filter" item: Radix rejects an empty item value, so the
// state keeps `undefined` and only the control sees this.
const ALL_VALUE = "__all__";
// A group without an error code, as a filter value.
const NO_CODE = "__none__";

const codeOf = (group: ReloadErrorGroup) => group.errorCode || NO_CODE;
const groupId = (group: ReloadErrorGroup) => `${group.errorCode}|${group.message}`;

// Within a day a moment reads better relative ("5 minutes ago"), older ones as a date.
const isRecent = (iso: string) => Date.now() - new Date(iso).getTime() < 86_400_000;

type AppFilters = { situation?: Situation };
type GroupFilters = { code?: string };

const initialState = {
  page: 1,
  pageSize: PAGE_SIZE,
  query: "",
  filters: {},
  selection: [],
} satisfies DataTableState;

function Section({ title, note, children }: { title: string; note: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        <span className="text-xs text-dim">{note}</span>
      </div>
      {children}
    </section>
  );
}

export function ErrorsView({
  connectionId,
  baseUrl,
  period,
  version,
  onViewRuns,
}: {
  connectionId: string;
  baseUrl: string;
  period: ReloadPeriodParams;
  version: string;
  onViewRuns: (appId: string) => void;
}) {
  const { t } = useTranslation("reloads");
  const { t: tc } = useTranslation("common");
  const { language, dateFnsLocale } = useLocale();
  const { formatDate, formatDateTime } = useFormatDate();
  const { density, setDensity } = useTableDensity();
  const searchRef = useRef<HTMLInputElement>(null);
  useSearchHotkey(searchRef);

  const query = useQuery(reloadErrorsQueryOptions(connectionId, period, version));
  const apps = query.data?.apps ?? [];
  const groups = query.data?.groups ?? [];

  const [appsState, setAppsState] = useState<DataTableState<AppFilters>>(initialState);
  const [groupsState, setGroupsState] = useState<DataTableState<GroupFilters>>(initialState);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  const count = (value: number) => value.toLocaleString(language);
  const percent = (ratio: number) =>
    ratio.toLocaleString(language, { style: "percent", maximumFractionDigits: 1 });
  const kpi = (value?: number) => (value === undefined ? "—" : count(value));
  const when = (iso: string) => (
    <time dateTime={iso} title={formatDateTime(iso)}>
      {isRecent(iso)
        ? formatDistanceToNowStrict(new Date(iso), { addSuffix: true, locale: dateFnsLocale })
        : formatDate(iso)}
    </time>
  );

  const appColumns: DataTableColumn<ReloadAppErrors>[] = [
    {
      id: "app",
      header: t("errors.failingApps.columns.app"),
      variant: "identity",
      rowHeader: true,
      sort: "text",
      value: appLabel,
      cell: (app) => {
        const reason = shortFailureReason(app.lastErrorCode, app.lastErrorMessage);
        return (
          <TableIdentity
            name={<QlikAppLink app={app} baseUrl={baseUrl} />}
            detail={
              reason && (
                <span
                  className="text-status-danger-foreground"
                  title={failureReason(app.lastErrorCode, app.lastErrorMessage)}
                >
                  {reason}
                </span>
              )
            }
          />
        );
      },
    },
    {
      id: "situation",
      header: t("errors.failingApps.columns.situation"),
      width: "10rem",
      cell: (app) => {
        const situation = situationOf(app);
        return (
          <StatusBadge tone={SITUATION_TONE[situation]}>
            {t(`errors.failingApps.situation.${situation}`)}
          </StatusBadge>
        );
      },
    },
    {
      id: "failed",
      header: t("errors.failingApps.columns.failures"),
      width: "9.5rem",
      align: "end",
      variant: "numeric",
      sort: "number",
      value: (app) => app.failed,
      cell: (app) =>
        t("errors.failingApps.failedOf", { failed: count(app.failed), total: count(app.total) }),
    },
    {
      id: "rate",
      header: t("errors.failingApps.columns.rate"),
      width: "9.5rem",
      sort: "number",
      value: (app) => app.failed / app.total,
      cell: (app) => (
        <span className="flex items-center gap-2">
          <Progress
            value={app.failed}
            max={app.total}
            tone="danger"
            size="md"
            className="w-16"
            aria-hidden
          />
          <Mono>{percent(app.failed / app.total)}</Mono>
        </span>
      ),
    },
    {
      id: "streak",
      header: t("errors.failingApps.columns.streak"),
      width: "10.5rem",
      align: "end",
      variant: "numeric",
      sort: "number",
      value: (app) => app.currentStreak,
      cell: (app) => count(app.currentStreak),
    },
    {
      id: "lastSuccess",
      header: t("errors.failingApps.columns.lastSuccess"),
      width: "10.5rem",
      align: "end",
      variant: "numeric",
      cell: (app) => (app.lastSuccessAt ? when(app.lastSuccessAt) : "—"),
    },
  ];

  const groupColumns: DataTableColumn<ReloadErrorGroup>[] = [
    {
      id: "code",
      header: t("errors.groups.columns.code"),
      width: "13rem",
      cell: (group) => (group.errorCode ? <Mono>{group.errorCode}</Mono> : "—"),
    },
    {
      id: "message",
      header: t("errors.groups.columns.message"),
      // Two lines say what failed; the whole message stays in the tooltip.
      wrap: true,
      cell: (group) =>
        group.message ? (
          <Mono className="line-clamp-2" title={group.message}>
            {group.message}
          </Mono>
        ) : (
          "—"
        ),
    },
    {
      id: "count",
      header: t("errors.groups.columns.count"),
      width: "9.5rem",
      align: "end",
      variant: "numeric",
      sort: "number",
      value: (group) => group.count,
      cell: (group) => count(group.count),
    },
    {
      id: "apps",
      header: t("errors.groups.columns.apps"),
      width: "14rem",
      cell: (group) => {
        const names = group.apps.map(appLabel).join(", ");
        const more = group.appCount - group.apps.length;
        return (
          <span title={names}>
            {group.apps.map((app, i) => (
              <Fragment key={app.appId}>
                {i > 0 && ", "}
                <QlikAppLink app={app} baseUrl={baseUrl} />
              </Fragment>
            ))}
            {more > 0 && <span className="ml-1 text-xs text-faint">+{more}</span>}
          </span>
        );
      },
    },
    {
      id: "last",
      header: t("errors.groups.columns.last"),
      width: "9.5rem",
      align: "end",
      variant: "numeric",
      cell: (group) => when(group.lastAt),
    },
  ];

  // The whole message, when it began and each app it hit, with a way into the
  // app's runs.
  const groupDetail = (group: ReloadErrorGroup) => {
    const more = group.appCount - group.apps.length;
    return (
      <div className="grid gap-4 py-2 md:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-2">
          <span className="text-xs font-medium text-dim">{t("errors.groups.detail.message")}</span>
          {group.message ? (
            <CopyField
              value={group.message}
              copyLabel={t("errors.groups.detail.copy")}
              copiedLabel={t("errors.groups.detail.copied")}
              onCopyError={() => toast.error(t("errors.groups.detail.copyFailed"))}
            >
              <span className="whitespace-pre-wrap">{group.message}</span>
            </CopyField>
          ) : (
            "—"
          )}
          <span className="text-xs text-dim">
            {t("errors.groups.detail.first", { when: formatDateTime(group.firstAt) })}
          </span>
        </div>
        <div className="flex min-w-0 flex-col gap-2">
          <span className="text-xs font-medium text-dim">{t("errors.groups.detail.apps")}</span>
          <ul className="divide-y divide-hairline-soft rounded-md border border-hairline bg-surface">
            {group.apps.map((app) => (
              <li key={app.appId} className="flex items-center gap-3 px-3 py-1.5 text-sm">
                <span className="min-w-0 flex-1">
                  <QlikAppLink app={app} baseUrl={baseUrl} />
                </span>
                <Mono className="text-dim">
                  {t("errors.groups.detail.times", {
                    count: app.count,
                    formatted: count(app.count),
                  })}
                </Mono>
                <span className="w-24 text-right text-xs text-dim">{when(app.lastAt)}</span>
                <ViewRunsButton app={app} onClick={() => onViewRuns(app.appId)} />
              </li>
            ))}
          </ul>
          {more > 0 && (
            <span className="text-xs text-dim">
              {t("errors.groups.detail.more", { count: more, formatted: count(more) })}
            </span>
          )}
        </div>
      </div>
    );
  };

  const appsPage = localTablePage(apps, appsState, {
    columns: appColumns,
    search: (app) =>
      [appLabel(app), failureReason(app.lastErrorCode, app.lastErrorMessage)].join(" "),
    filter: (app, { situation }) => !situation || situationOf(app) === situation,
  });
  const groupsPage = localTablePage(groups, groupsState, {
    columns: groupColumns,
    search: (group) => [group.message, ...group.apps.map(appLabel)].join(" "),
    filter: (group, { code }) => !code || codeOf(group) === code,
  });
  const codes = [...new Set(groups.map(codeOf))].sort();

  const setSituation = (situation?: Situation) =>
    setAppsState((state) => updateTableState(state, { filters: { situation } }));
  const setCode = (code?: string) =>
    setGroupsState((state) => updateTableState(state, { filters: { code } }));

  const supportInfo = supportCode(query.error, tc("error.supportCode"));
  const error = query.isError
    ? {
        title: t("error.load"),
        description: supportInfo && (
          <>
            {supportInfo.label}: <Mono>{supportInfo.value}</Mono>
          </>
        ),
      }
    : null;
  const messages = tableMessages({ empty: { title: t("errors.empty") } });

  return (
    <div className="flex flex-col gap-4">
      <KpiStrip>
        <KpiCell
          label={t("errors.kpi.total")}
          value={kpi(query.data?.total)}
          subMeta={t("errors.kpi.totalHint")}
        />
        <KpiCell
          label={t("errors.kpi.failed")}
          value={kpi(query.data?.failed)}
          subMeta={t("errors.kpi.failedHint")}
        />
        <KpiCell
          label={t("errors.kpi.rate")}
          value={query.data?.total ? percent(query.data.failed / query.data.total) : "—"}
        />
        <KpiCell label={t("errors.kpi.apps")} value={kpi(query.data?.apps.length)} />
      </KpiStrip>

      <Section title={t("errors.failingApps.title")} note={t("errors.failingApps.note")}>
        <DataTable
          label={t("errors.failingApps.title")}
          density={density}
          columns={appColumns}
          rows={appsPage.rows}
          getRowId={(app) => app.appId}
          state={appsState}
          onStateChange={setAppsState}
          loading={query.isPending}
          refreshing={listRefreshing(query)}
          error={error}
          onRetry={() => void query.refetch()}
          search={{
            ...tableSearch(t("errors.failingApps.searchPlaceholder")),
            inputRef: searchRef,
            shortcutHint: "/",
          }}
          toolbar={
            <>
              <Select
                value={appsState.filters.situation ?? ALL_VALUE}
                onValueChange={(value) => setSituation(SITUATIONS.find((s) => s === value))}
              >
                <SelectTrigger
                  size="compact"
                  className="w-48"
                  aria-label={t("errors.failingApps.situationLabel")}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_VALUE}>{t("errors.failingApps.situationAll")}</SelectItem>
                  {SITUATIONS.map((situation) => (
                    <SelectItem key={situation} value={situation}>
                      {t(`errors.failingApps.situation.${situation}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DensityToggle value={density} onChange={setDensity} />
            </>
          }
          rowActions={{
            label: t("actions.column"),
            width: 56,
            render: (app) => <ViewRunsButton app={app} onClick={() => onViewRuns(app.appId)} />,
          }}
          pagination={
            appsPage.total > PAGE_SIZE
              ? { kind: "offset", total: appsPage.total, labels: paginationLabels() }
              : undefined
          }
          messages={messages}
        />
      </Section>

      <Section title={t("errors.groups.title")} note={t("errors.groups.note")}>
        <DataTable
          label={t("errors.groups.title")}
          density={density}
          columns={groupColumns}
          rows={groupsPage.rows}
          getRowId={groupId}
          state={groupsState}
          onStateChange={setGroupsState}
          expansion={{
            expanded: expandedGroups,
            onExpandedChange: setExpandedGroups,
            render: groupDetail,
            getRowLabel: (group) => failureReason(group.errorCode, group.message),
            labels: {
              column: t("errors.groups.detail.column"),
              expand: (label) => t("errors.groups.detail.expand", { label }),
              collapse: (label) => t("errors.groups.detail.collapse", { label }),
            },
          }}
          loading={query.isPending}
          refreshing={listRefreshing(query)}
          error={error}
          onRetry={() => void query.refetch()}
          search={tableSearch(t("errors.groups.searchPlaceholder"))}
          toolbar={
            <>
              <Select
                value={groupsState.filters.code ?? ALL_VALUE}
                onValueChange={(value) => setCode(value === ALL_VALUE ? undefined : value)}
              >
                <SelectTrigger
                  size="compact"
                  className="w-52"
                  aria-label={t("errors.groups.codeLabel")}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_VALUE}>{t("errors.groups.codeAll")}</SelectItem>
                  {codes.map((code) => (
                    <SelectItem key={code} value={code}>
                      {code === NO_CODE ? t("errors.groups.noCode") : code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DensityToggle value={density} onChange={setDensity} />
            </>
          }
          pagination={
            groupsPage.total > PAGE_SIZE
              ? { kind: "offset", total: groupsPage.total, labels: paginationLabels() }
              : undefined
          }
          messages={messages}
        />
      </Section>
    </div>
  );
}
